// MUI component overrides — consume tokens, never hardcode hex values here.
// This module is a function so it can access the resolved theme palette.

import { RADIUS } from './tokens';

export function buildComponentOverrides(mode) {
  const isLight = mode === 'light';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: isLight
            ? 'rgba(15,23,42,0.2) transparent'
            : 'rgba(255,255,255,0.15) transparent',
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: isLight
            ? 'rgba(15,23,42,0.18)'
            : 'rgba(255,255,255,0.14)',
          borderRadius: 6,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-corner': { background: 'transparent' },
        // Gradient animation keyframes — used by GradientSurface
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
        '@keyframes bmlPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: RADIUS.md,
          padding: '8px 20px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: ({ theme }) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.light ?? theme.palette.primary.main} 0%, ${theme.palette.primary.main} 100%)`,
          },
        }),
        outlined: {
          borderWidth: 2,
          '&:hover': { borderWidth: 2 },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: RADIUS.lg,
          boxShadow: isLight
            ? '0 4px 20px rgba(15,23,42,0.08)'
            : '0 4px 20px rgba(0,0,0,0.3)',
          backgroundImage: 'none',
          backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(18,26,43,0.62)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: isLight ? '1px solid rgba(15,23,42,0.06)' : `1px solid ${theme.palette.divider}`,
          '&[data-aurora-opaque="true"]': {
            backgroundColor: theme.palette.background.paper,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: ({ theme }) => ({
          boxShadow: isLight
            ? '0 2px 6px rgba(15,23,42,0.06)'
            : '0 2px 8px rgba(0,0,0,0.4)',
          backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(18,26,43,0.62)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          '&[data-aurora-opaque="true"]': {
            backgroundColor: theme.palette.background.paper,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        }),
        elevation2: ({ theme }) => ({
          boxShadow: isLight
            ? '0 8px 20px rgba(15,23,42,0.08)'
            : '0 10px 24px rgba(0,0,0,0.45)',
          backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(18,26,43,0.62)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          '&[data-aurora-opaque="true"]': {
            backgroundColor: theme.palette.background.paper,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        }),
        elevation3: ({ theme }) => ({
          boxShadow: isLight
            ? '0 16px 40px rgba(15,23,42,0.10)'
            : '0 20px 48px rgba(0,0,0,0.55)',
          backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(18,26,43,0.62)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          '&[data-aurora-opaque="true"]': {
            backgroundColor: theme.palette.background.paper,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        }),
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 2px 12px rgba(15,23,42,0.12)',
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm,
          fontWeight: 500,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 48,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          height: 3,
          borderRadius: '3px 3px 0 0',
        }),
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: RADIUS.md,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
        }),
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused': { color: theme.palette.primary.main },
        }),
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          backgroundColor: isLight
            ? 'rgba(242,101,34,0.12)'
            : 'rgba(255,138,80,0.14)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent?.main ?? theme.palette.warning.main} 100%)`,
          },
        }),
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },

    MuiFab: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.light ?? theme.palette.primary.main} 0%, ${theme.palette.primary.main} 100%)`,
          },
        }),
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: ({ theme }) => ({
          '&.Mui-checked': {
            color: theme.palette.primary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }),
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: RADIUS.xl,
          backgroundColor: theme.palette.background.paper,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        }),
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        }),
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: '#FFFFFF',
        }),
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: RADIUS.md,
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            '&:hover': { backgroundColor: theme.palette.action.selected },
          },
        }),
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: '#FFFFFF',
          fontSize: '0.75rem',
        }),
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&:hover': { backgroundColor: theme.palette.action.hover },
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            '&:hover': { backgroundColor: theme.palette.action.selected },
          },
        }),
      },
    },

    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: isLight ? '#ECFDF5' : 'rgba(74,222,128,0.1)',
        },
        standardError: {
          backgroundColor: isLight ? '#FEF2F2' : 'rgba(248,113,113,0.1)',
        },
        standardWarning: {
          backgroundColor: isLight ? '#FFFBEB' : 'rgba(245,158,11,0.1)',
        },
        standardInfo: {
          backgroundColor: isLight ? '#F0F9FF' : 'rgba(56,189,248,0.1)',
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }),
        head: ({ theme }) => ({
          backgroundColor: isLight
            ? 'rgba(15,23,42,0.06)'
            : 'rgba(255,255,255,0.06)',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }),
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: isLight ? 'rgba(255,255,255,0.72)' : 'rgba(18,26,43,0.62)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: `1px solid ${theme.palette.divider}`,
        }),
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-selected': { color: theme.palette.primary.main },
        }),
      },
    },

    // Register the custom 'display' variant so MUI Typography applies the
    // theme.typography.display styles (fontSize 2.5rem, fontWeight 800, etc.)
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          display: 'p',
        },
      },
      variants: [
        {
          props: { variant: 'display' },
          style: ({ theme }) => ({
            ...theme.typography.display,
          }),
        },
      ],
    },
  };
}
